import React, { useEffect, useState } from 'react';

import { Link, useNavigate } from '@reach/router';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import genericModelTypes from '../../../provider/models/genericModelTypes';
import { sendPost } from '../../../provider/models/reservationModel';
import { formatDate } from '../../../provider/utility';
import {
  changedContactForm,
  changedKnownClient,
  sentForm,
} from '../../../store/actions';
import { serviceTypes } from '../../../store/initData/services';
import { env } from '../../../utils/env';
import AccordionFormField from '../../AccordionFormField';
import AccordionOptionPayment from '../../AccordionOptionPayment/AccordionOptionPayment';
import BackButton from '../../BackButton/BackButton';
import Button from '../../Button/Button';
import DatePickerFormField from '../../DatePickerFormField/DatePickerFormField';
import FormField from '../../FormField/FormField';
import { validate } from '../../FormField/helpers';
import FormLabel from '../../FormLabel/FormLabel';
import SingleInputPage from '../SingleInputPage/SingleInputPage';
import styles from '../SingleInputPage/SingleInputPage.module.scss';
import './FormPage.scss';
import FormPageAgreement from './FormPageAgreement/FormPageAgreement';
import WeekDayPicker from './WeekDayPicker';
import { validateWeekDays } from './WeekDayPicker/helpers';
import { summerplayToWeekDays } from './helpers';

const summerplayAgreementMessage =
  'Oświadczam, że zapoznałem/am się i akceptuję Regulamin serwisu oraz Regulamin półkolonii. ' +
  'Przyjmuję do wiadomości zasady organizacji wypoczynku oraz przetwarzania moich danych osobowych ' +
  'zawarte w tych dokumentach, co jest równoznaczne z wyrażeniem zgody na świadczenie usług ' +
  'niezbędnych do realizacji procesu zapisów drogą elektroniczną.';
const healthContraindicationsMessage =
  'Oświadczam, że dziecko nie ma przeciwwskazań zdrowotnych do udziału w zajęciach.';

function FormPage({ addressInput }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasAllergies, setHasAllergies] = useState(null);
  const [hasSpecialNeeds, setSpecialNeeds] = useState(null);
  const [summerplayAgreementChecked, setSummerplayAgreementChecked] =
    useState(false);
  const [healthContraindicationsChecked, setHealthContraindicationsChecked] =
    useState(false);

  const currentPage = useSelector((store) => store.state.page);
  const currentService = useSelector((store) => store.state.service);
  const isWorkshopService = currentService.value === serviceTypes.workshop;
  const isSummerplayService = currentService.value === serviceTypes.summerplay;
  const isOneTimeWorkshopService = currentService.value === serviceTypes.one_time_workshop;
  const currentFranchise = useSelector((store) => store.state.city?.franchise);
  const currentRobot = useSelector((store) => store.state.robot);

  const currentAge = useSelector((store) => store.state.age);
  const currentOccurrence = useSelector((store) => store.state.occurrence);
  const currentForm = useSelector((store) => store.state.contactForm);
  const agreements = useSelector((store) => store.state.agreements);

  const formPageTexts = useSelector((store) => store.data.formPage);
  const allPaymentOptions = useSelector((store) => store.data.paymentOptions);
  const sendText = useSelector((store) => store.data.sendText);

  const availablePaymentOptions =
    currentOccurrence?.payment_type === 'kindergarden_vat_invoice'
      ? allPaymentOptions.filter(
          (po) => po.value === 'kindergarden_vat_invoice',
        )
      : allPaymentOptions.filter(
          (po) => po.value !== 'kindergarden_vat_invoice',
        );

  const isParentPaymentForm =
    currentOccurrence?.payment_type !== 'kindergarden_vat_invoice';

  const [weekDays, setWeekDays] = useState(
    summerplayToWeekDays(currentOccurrence),
  );
  useEffect(() => {
    if (currentOccurrence) {
      setWeekDays(summerplayToWeekDays(currentOccurrence));
    }
  }, [currentOccurrence]);

  const handleSend = (type) => {
    setLoading(true);

    let data = {
      id: currentOccurrence?.id || '',
      //   genericType: genericModelTypes.suggestedPlace,
      genericType: genericModelTypes.suggestedPlaceNew,
      genericInfo: {
        service: currentService,
        suggestedAddress: currentForm.address?.text,
        franchise: currentFranchise,
        level: currentRobot,
        age: currentAge,
      },
      fullName: currentForm.name.text,
      school_class: currentForm.schoolClass.text,
      parent_full_name: currentForm.parentFullName.text,
      email: currentForm.email.text,
      birthday: formatDate(currentForm.date.text),
      mobile: currentForm.phone.text,
      days: weekDays
        .filter((day) => day.checked)
        .map((day) => format(day.date, 'yyyy-MM-dd')),
      known_client: currentForm.knownClient.value,
      comment: currentForm.comment.text,
      tos: isSummerplayService
        ? summerplayAgreementChecked
          ? summerplayAgreementMessage
          : ''
        : agreements[0].checked
          ? agreements[0].message
          : '',
      policy: agreements[1].checked ? agreements[1].message : '',
      marketing: agreements[2].checked ? agreements[2].message : '',
      payment_type:
        currentOccurrence !== null &&
        currentOccurrence.payment_type === 'kindergarden_vat_invoice'
          ? 'kindergarden_vat_invoice'
          : currentForm.paymentType.text.value,
      postal_code: currentForm.postalCode.text,
      street_address: currentForm.streetAddress.text,
      city: currentForm.city.text,
      promo_coupon: currentForm.promoCoupon.text,
      allergies: hasAllergies ? currentForm.allergies?.text : null,
      special_needs: hasSpecialNeeds ? currentForm.specialNeeds?.text : null,
      health_contraindications: healthContraindicationsChecked
        ? healthContraindicationsMessage
        : null,
    };

    sendPost(data, type, formPageTexts.errorInfo).then(({ success, status }) => {
      if (success) {
        dispatch(sentForm(type));
        navigate(`${env.PUBLIC_URL}/thanks`, {state : {status: status}});
      } else {
        setLoading(false);
      }
    });
  };

  const addressInputComp = (
    <>
      <FormLabel>{formPageTexts.offerText}</FormLabel>
      <FormField
        type={'address'}
        hint={'np. Szkoła nr 56'}
        value={currentForm.address.text}
        valid={currentForm.address.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'address', text: value, valid: valid }),
          )
        }
      />
    </>
  );

  const isCheckedAgreements = agreements
    .filter(
      (item) =>
        item.is_required && (!isSummerplayService || item.order !== 1),
    )
    .every((item) => item.checked);
  const areRequiredAgreementsChecked =
    isCheckedAgreements &&
    (!isSummerplayService || summerplayAgreementChecked) &&
    (!currentOccurrence?.health_contraindications_enabled ||
      healthContraindicationsChecked);

  const isFilled = () => {
    const isAllergiesValid =
      hasAllergies === false ||
      (hasAllergies === true && currentForm.allergies?.valid);
    const isSpecialNeedsValid =
      hasSpecialNeeds === false ||
      (hasSpecialNeeds === true && currentForm.specialNeeds?.valid);

    if (addressInput) {
      return (
        currentForm.address.valid &&
        currentForm.date.valid &&
        currentForm.name.valid &&
        currentForm.email.valid &&
        currentForm.phone.valid &&
        // currentForm.knownClient.valid &&
        areRequiredAgreementsChecked
      );
    }

    if (isWorkshopService && isParentPaymentForm) {
      return (
        currentForm.date.valid &&
        currentForm.name.valid &&
        currentForm.email.valid &&
        currentForm.phone.valid &&
        currentForm.parentFullName.valid &&
        currentForm.paymentType.valid &&
        currentForm.streetAddress.valid &&
        currentForm.postalCode.valid &&
        currentForm.city.valid &&
        currentForm.knownClient.valid &&
        // currentForm.promoCoupon.valid &&
        areRequiredAgreementsChecked
      );
    }

    if (isWorkshopService) {
      return (
        currentForm.date.valid &&
        currentForm.name.valid &&
        currentForm.email.valid &&
        currentForm.phone.valid &&
        currentForm.paymentType.valid &&
        // currentForm.knownClient.valid &&
        areRequiredAgreementsChecked
      );
    }

    if(isOneTimeWorkshopService) {
      return (
        currentForm.date.valid &&
        currentForm.name.valid &&
        currentForm.email.valid &&
        currentForm.phone.valid &&
        areRequiredAgreementsChecked
      );
    }
    // summerplay

    return (
      currentForm.date.valid &&
      currentForm.name.valid &&
      validateWeekDays(weekDays) &&
      currentForm.email.valid &&
      currentForm.phone.valid &&
      currentForm.parentFullName.valid &&
      isAllergiesValid &&
      isSpecialNeedsValid &&
      // currentForm.knownClient.valid &&
      areRequiredAgreementsChecked
    );
  };

  let sentButton;
  if (addressInput) {
    sentButton = (
      <Button
        disabled={!isFilled()}
        type={'primary'}
        text={sendText}
        onClick={() => handleSend('generic')}
      />
    );
  } else {
    sentButton = (
      <Button
        disabled={!isFilled()}
        type={'primary'}
        text={sendText}
        onClick={() =>
          handleSend(isWorkshopService ? 'workshop' : isOneTimeWorkshopService ? 'one_time_workshop' :'summerplay')
        }
      />
    );
  }

  const addressFields = (
    <>
      <FormLabel required>Rodzic: Imię i nazwisko</FormLabel>
      <FormField
        type={'parentFullName'}
        hint={'Anna Kowalska'}
        value={currentForm.parentFullName.text}
        valid={currentForm.parentFullName.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({
              field: 'parentFullName',
              text: value,
              valid: valid,
            }),
          )
        }
      />

      <FormLabel required>Adres</FormLabel>
      <FormField
        type={'streetAddress'}
        hint={'Ulica i numer domu'}
        value={currentForm.streetAddress.text}
        valid={currentForm.streetAddress.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({
              field: 'streetAddress',
              text: value,
              valid: valid,
            }),
          )
        }
      />

      <FormLabel required>Kod pocztowy</FormLabel>
      <FormField
        type={'postalCode'}
        hint={'w formacie 00-000'}
        value={currentForm.postalCode.text}
        valid={currentForm.postalCode.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({
              field: 'postalCode',
              text: value,
              valid: valid,
            }),
          )
        }
      />

      <FormLabel required>Miasto</FormLabel>
      <FormField
        type={'city'}
        hint={''}
        value={currentForm.city.text}
        valid={currentForm.city.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'city', text: value, valid: valid }),
          )
        }
      />
    </>
  );

  const content = (
    <>
      <FormLabel type={'big'}>{formPageTexts.mainLabel}</FormLabel>
      {isWorkshopService ? (
        <FormLabel addStyle={{ margin: 0 }}>{formPageTexts.whyData}</FormLabel>
      ) : null}
      {addressInput ? addressInputComp : null}

      <FormLabel required>{formPageTexts.nameLabel}</FormLabel>
      <FormField
        type={'name'}
        hint={formPageTexts.nameHint}
        value={currentForm.name.text}
        valid={currentForm.name.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'name', text: value, valid: valid }),
          )
        }
      />
      <FormLabel required>{formPageTexts.datapickerLabel}</FormLabel>
      <DatePickerFormField
        value={currentForm.date.text ? currentForm.date.text : ''}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'date', text: value, valid: valid }),
          )
        }
        valid={currentForm.date.valid}
        age={currentAge?.value}
      />

      {!isWorkshopService && !isOneTimeWorkshopService && !currentOccurrence?.only_all_week && (
        <>
          <FormLabel>{formPageTexts.weekDaysLabel}</FormLabel>
          <WeekDayPicker
            data={weekDays}
            changeFnc={setWeekDays}
            isValid={validateWeekDays(weekDays)}
          />
        </>
      )}

      <FormLabel required>{formPageTexts.emailLabel}</FormLabel>
      <FormField
        type={'email'}
        hint={formPageTexts.emailHint}
        value={currentForm.email.text}
        valid={currentForm.email.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'email', text: value, valid: valid }),
          )
        }
      />
      <FormLabel required>{formPageTexts.phoneLabel}</FormLabel>
      <FormField
        type={'phone'}
        hint={formPageTexts.phoneHint}
        value={currentForm.phone.text}
        valid={currentForm.phone.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'phone', text: value, valid: valid }),
          )
        }
      />

      {isWorkshopService && !addressInput && (
        <>
          <FormLabel required>
            Forma płatności (w przypadku podpisania umowy)
          </FormLabel>

          <AccordionOptionPayment
            type={'paymentType'}
            data={availablePaymentOptions}
            selected={currentForm.paymentType.text}
            valid={currentForm.paymentType.valid}
            onClick={(value) => {
              dispatch(
                changedContactForm({
                  field: 'paymentType',
                  text: value,
                  valid: validate(value.value, 'paymentType') === 'success',
                }),
              );
            }}
            placeholder={''}
          />
        </>
      )}

      {isParentPaymentForm && isWorkshopService && !addressInput
        ? addressFields
        : <>
        {addressFields.props.children[0]}
        {addressFields.props.children[1]}
      </>
      } {}

      {!isWorkshopService && !isOneTimeWorkshopService && (
        <>
          <div style={{ position: 'relative', zIndex: 30 }}>
            <FormLabel required>Czy dziecko ma alergie?</FormLabel>
            <AccordionFormField
              formType
              data={[
                { text: 'Tak', value: true },
                { text: 'Nie', value: false },
              ]}
              selected={
                hasAllergies !== null
                  ? { text: hasAllergies ? 'Tak' : 'Nie', value: hasAllergies }
                  : ''
              }
              onClick={(item) => {
                setHasAllergies(item.value);
                if (item.value === false) {
                    dispatch(
                      changedContactForm({
                        field: 'allergies',
                        text: '',
                        valid: true,
                      })
                    );
                  } else {
                    dispatch(
                      changedContactForm({
                        field: 'allergies',
                        text: currentForm.allergies?.text || '',
                        valid: !!currentForm.allergies?.text,
                      })
                    );
                  }
              }}
              placeholder={''}
            />

            {hasAllergies == true && (
              <>
                <FormLabel required>Jakie to alergie?</FormLabel>
                <FormField
                  type={'allergies'}
                  hint={formPageTexts.allergyHint}
                  value={currentForm.allergies?.text || ''}
                  valid={currentForm.allergies?.valid}
                  onChange={(value, valid) =>
                    dispatch(
                      changedContactForm({
                        field: 'allergies',
                        text: value,
                        valid: valid,
                      }),
                    )
                  }
                />
              </>
            )}
            </div>

            <div style={{ position: 'relative', zIndex: 20 }}>
            <FormLabel required>Czy dziecko ma specjalne potrzeby edukacyjne?</FormLabel>
            <AccordionFormField
              formType
              data={[
                { text: 'Tak', value: true },
                { text: 'Nie', value: false },
              ]}
              selected={
                hasSpecialNeeds !== null
                  ? { text: hasSpecialNeeds ? 'Tak' : 'Nie', value: hasSpecialNeeds }
                  : ''
              }
              onClick={(item) => {
                setSpecialNeeds(item.value);
                if (item.value === false) {
                    dispatch(
                      changedContactForm({
                        field: 'specialNeeds',
                        text: '',
                        valid: true,
                      })
                    );
                  } else {
                    dispatch(
                      changedContactForm({
                        field: 'specialNeeds',
                        text: currentForm.specialNeeds?.text || '',
                        valid: !!currentForm.specialNeeds?.text,
                      })
                    );
                  }
              }}
              placeholder={''}
            />

            {hasSpecialNeeds == true && (
              <>
                <FormLabel required>Jakie to potrzeby?</FormLabel>
                <FormField
                  type={'specialNeeds'}
                  hint={formPageTexts.specialNeedsHint}
                  value={currentForm.specialNeeds?.text || ''}
                  valid={currentForm.specialNeeds?.valid}
                  onChange={(value, valid) =>
                    dispatch(
                      changedContactForm({
                        field: 'specialNeeds',
                        text: value,
                        valid: valid,
                      }),
                    )
                  }
                />
              </>
            )}
          </div>
        </>
      )}

      {isWorkshopService && (
        <>
          <FormLabel>{formPageTexts.schoolClassLabel}</FormLabel>
          <FormField
            value={currentForm.schoolClass.text}
            valid={currentForm.schoolClass.valid}
            onChange={(value, valid) =>
              dispatch(
                changedContactForm({
                  field: 'schoolClass',
                  text: value,
                  valid: valid,
                }),
              )
            }
          />
        </>
      )}

      <FormLabel required>{formPageTexts.knownClientLabel}</FormLabel>
      <AccordionFormField
        formType
        data={[
          { text: 'Tak', value: true },
          { text: 'Nie', value: false },
        ]}
        selected={currentForm.knownClient}
        onClick={(item) =>
          dispatch(
            changedKnownClient({
              field: 'knownClient',
              text: item.text,
              value: item.value,
              valid: true,
            }),
          )
        }
        placeholder={formPageTexts.knownClientHint}
      />

      {currentForm.knownClient.value && (
        <>
          <FormLabel>Kupon promocyjny (jeżeli posiadasz)</FormLabel>
          <FormField
            hint={''}
            value={currentForm.promoCoupon.text}
            valid={currentForm.promoCoupon.valid}
            onChange={(value, valid) =>
              dispatch(
                changedContactForm({
                  field: 'promoCoupon',
                  text: value,
                  valid: valid,
                }),
              )
            }
          />
        </>
      )}

      <FormLabel>{formPageTexts.commentLabel}</FormLabel>
      <FormField
        value={currentForm.comment.text}
        valid={currentForm.comment.valid}
        onChange={(value, valid) =>
          dispatch(
            changedContactForm({ field: 'comment', text: value, valid: valid }),
          )
        }
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <FormPageAgreement
          agreements={agreements}
          additionalAgreement={
            currentOccurrence?.health_contraindications_enabled
              ? {
                  checked: healthContraindicationsChecked,
                  onChange: () =>
                    setHealthContraindicationsChecked((checked) => !checked),
                  message: healthContraindicationsMessage,
                }
              : null
          }
          replacementAgreement={
            isSummerplayService
              ? {
                  replacedOrder: 1,
                  checked: summerplayAgreementChecked,
                  onChange: () =>
                    setSummerplayAgreementChecked((checked) => !checked),
                  message: (
                    <>
                      Oświadczam, że zapoznałem/am się i akceptuję{' '}
                      <a
                        className="FormPage__link"
                        href="https://www.go4robot.pl/regulamin"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Regulamin serwisu
                      </a>{' '}
                      oraz{' '}
                      {currentFranchise.summerplay_terms_pdf_url ? (
                        <a
                          className="FormPage__link"
                          href={currentFranchise.summerplay_terms_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Regulamin półkolonii
                        </a>
                      ) : (
                        'Regulamin półkolonii'
                      )}
                      . Przyjmuję do wiadomości zasady organizacji wypoczynku
                      oraz przetwarzania moich danych osobowych zawarte w tych
                      dokumentach, co jest równoznaczne z wyrażeniem zgody na
                      świadczenie usług niezbędnych do realizacji procesu
                      zapisów drogą elektroniczną.
                    </>
                  ),
                }
              : null
          }
        />
      </div>
      <div className={styles.navWrapper}>
        <Link to={'../'}>
          <BackButton />
        </Link>
        {sentButton}
      </div>
    </>
  );

  return (
    <SingleInputPage
      page={currentPage}
      currentStep={currentPage.step}
      content={content}
      noTiles
      backButton={false}
      nextButton={false}
      loading={loading}
    />
  );
}

export default FormPage;
